import type { Side } from './hands';
import type { Blockers } from './combos';

export type Sign = '+' | '-';

/** Where a term starts on each triangle: each side keeps its own running count across the terms of a question. */
export interface SideOffsets {
  offsuit: number;
  suited: number;
}
export const ZERO_OFFSETS: SideOffsets = { offsuit: 0, suited: 0 };

/** One `A × B` block of a question. */
export interface Term {
  a: number;
  b: number;
  sign: Sign;
  /** which matrix triangle(s) this term colours */
  side: Side;
  /** first hand (0-based, reading order) this term colours on each side it uses */
  from: SideOffsets;
  /** stripe region: the rank (index into RANKS) whose 12 hands this term lights, from the stripe's A end */
  rank?: number;
  /** blocked lines of the combo grid (suit indices 0–3), present only when a card of ours blocks them */
  blockedRows?: readonly number[];
  blockedCols?: readonly number[];
}

export interface Question {
  terms: Term[];
  /** Σ sign · a · b, always ≥ 1 */
  value: number;
  /** exactly `options` distinct positive integers, `value` at `correct` */
  options: number[];
  correct: number;
}

export interface Rng {
  /** uniform float in [0, 1) */
  next(): number;
  /** uniform integer in [lo, hi] inclusive */
  int(lo: number, hi: number): number;
  pick<T>(arr: readonly T[]): T;
  shuffle<T>(arr: T[]): T[];
}

/** Everything the generator needs, decoupled from the app config module. */
export interface GenParams {
  /** every A value a term can take (for the distractors); the generator derives each term's A from its side and blocked lines */
  a: readonly number[];
  /** lines of the combo grid blocked by our cards; the lines themselves are drawn per term */
  blockers: Blockers;
  bMin: number;
  bMax: number;
  bSumMax: number;
  terms: number;
  /** stripe region: every term draws its own rank and starts at the head of that stripe (no running count) */
  stripes?: boolean;
  operators: readonly Sign[];
  options: number;
  resultMin: number;
}
